import { closeHeaderContact } from "./header-contact";
import { closeMobileNav } from "./mobile-nav";

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function canHoverOpen() {
  return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
}

let closeTransitionHandler = null;

function clearCloseTransition(list) {
  if (closeTransitionHandler && list) {
    list.removeEventListener("transitionend", closeTransitionHandler);
  }
  closeTransitionHandler = null;
}

function setLangSwitcherOpen(root, open) {
  if (!root) return;

  const toggle = root.querySelector(".js-lang-toggle");
  const list = root.querySelector(".c-primary-nav__lang-list");

  if (list) {
    clearCloseTransition(list);
  }

  if (open) {
    if (list) {
      list.removeAttribute("hidden");
    }

    // Wait until [hidden] display:none is cleared before starting the transition
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        root.classList.add("is-open");
      });
    });
  } else {
    root.classList.remove("is-open");

    if (list) {
      const finishClose = () => {
        clearCloseTransition(list);
        list.setAttribute("hidden", "");
      };

      if (prefersReducedMotion()) {
        finishClose();
      } else {
        closeTransitionHandler = (event) => {
          if (event.target !== list) return;
          if (event.propertyName !== "max-height") return;
          finishClose();
        };
        list.addEventListener("transitionend", closeTransitionHandler);
      }
    }
  }

  if (toggle) {
    toggle.setAttribute("aria-expanded", open ? "true" : "false");
  }
}

function openLangSwitcher(root) {
  if (!root || root.classList.contains("is-open")) return;
  closeHeaderContact();
  closeMobileNav();
  setLangSwitcherOpen(root, true);
}

export function closeLangSwitcher() {
  const root = document.querySelector(".js-lang-switcher.is-open");
  if (root) {
    setLangSwitcherOpen(root, false);
  }
}

export function initLangSwitcher() {
  if (window.langSwitcherInitialized) {
    return;
  }

  window.langSwitcherInitialized = true;

  // Desktop: open/close on hover (header langs are replaced on Swup — use delegation)
  document.addEventListener("mouseover", (event) => {
    if (!canHoverOpen()) return;
    const root = event.target.closest?.(".js-lang-switcher");
    if (!root) return;

    const from = event.relatedTarget;
    if (from instanceof Node && root.contains(from)) return;

    openLangSwitcher(root);
  });

  document.addEventListener("mouseout", (event) => {
    if (!canHoverOpen()) return;
    const root = event.target.closest?.(".js-lang-switcher");
    if (!root) return;

    const to = event.relatedTarget;
    if (to instanceof Node && root.contains(to)) return;

    setLangSwitcherOpen(root, false);
  });

  // Keyboard: keep open while focus is inside the switcher
  document.addEventListener("focusin", (event) => {
    const root = event.target.closest?.(".js-lang-switcher");
    if (root) {
      openLangSwitcher(root);
    }
  });

  document.addEventListener("focusout", (event) => {
    const root = event.target.closest?.(".js-lang-switcher");
    if (!root) return;

    requestAnimationFrame(() => {
      if (!root.contains(document.activeElement)) {
        setLangSwitcherOpen(root, false);
      }
    });
  });

  // Touch / no-hover: toggle on click
  document.addEventListener("click", (event) => {
    const toggle = event.target.closest(".js-lang-toggle");
    const root = document.querySelector(".js-lang-switcher");

    if (toggle && root && root.contains(toggle)) {
      event.preventDefault();
      event.stopPropagation();

      // Hover devices already open on mouseover — don't toggle closed on click
      if (canHoverOpen()) {
        openLangSwitcher(root);
        return;
      }

      const willOpen = !root.classList.contains("is-open");
      if (willOpen) {
        openLangSwitcher(root);
      } else {
        setLangSwitcherOpen(root, false);
      }
      return;
    }

    if (root && root.classList.contains("is-open") && !root.contains(event.target)) {
      setLangSwitcherOpen(root, false);
    }
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      closeLangSwitcher();
    }
  });
}
